import React from 'react';
import { Controller } from 'react-hook-form';

import {
	Button,
	Grid,
	GridCol,
	Heading,
	Input,
	Popup,
	PopupSection,
	Spinner,
	Text,
	Textarea,
} from '@flixbus/honeycomb-react';

import CrewMemberAutoCompleteSingleV2 from 'common/CrewMemberAutocompleteSingleV2';

import { EDIT_ASSIGNMENT_TEXT } from './text';
import useEditAssignmentForm from './useEditAssignmentForm';

import styles from './styles.module.css';

const PopUpEditAssignment = ({
	onClose,
	onSubmit,
	stationLabel,
	defaultValues,
	loading = false,
	formId = 'edit-assignment-form',
	popupId = 'popup-edit-assignment',
}) => {
	const {
		control,
		departureDate,
		maxAllowedPastDate,
		isSubmitDisabled,
		departureDateRules,
		arrivalDateRules,
		arrivalTimeRules,
		revalidateArrivalTime,
		submitHandler,
	} = useEditAssignmentForm({ onSubmit, defaultValues });

	return (
		<Popup
			active
			id={popupId}
			aria-haspopup="dialog"
			extraClasses={styles.popup}
			onOverlayClick={onClose}
		>
			<PopupSection type="title" extraClasses={styles.marginBottom}>
				<Heading flushSpace Elem="h5" size={2}>
					{EDIT_ASSIGNMENT_TEXT.title}
				</Heading>
				<Text>{EDIT_ASSIGNMENT_TEXT.description}</Text>
				<Text extraClasses={styles.bolder}>{stationLabel}</Text>
			</PopupSection>

			{!loading && (
				<PopupSection type="content">
					<form id={formId} data-testid="edit-assignment-form" onSubmit={submitHandler}>
						<Grid applyContainer justify="left">
							<GridCol size={6}>
								<Grid applyContainer justify="space-between">
									<GridCol size={12}>
										<Heading flushSpace Elem="h5" size={3} extraClasses={styles.textAlign}>
											{EDIT_ASSIGNMENT_TEXT.actualDeparture}
										</Heading>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="departureDate"
											control={control}
											rules={departureDateRules}
											render={({ field: { ref: _ref, onChange, ...field }, fieldState }) => (
												<Input
													id="departureDate"
													type="date"
													label={EDIT_ASSIGNMENT_TEXT.dateLabel}
													max={maxAllowedPastDate}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
													onChange={(e) => {
														onChange(e);
														revalidateArrivalTime();
													}}
												/>
											)}
										/>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="departureTime"
											control={control}
											rules={{ required: true }}
											render={({ field: { ref: _ref, onChange, ...field } }) => (
												<Input
													id="departureTime"
													type="time"
													label={EDIT_ASSIGNMENT_TEXT.timeLabel}
													{...field}
													onChange={(e) => {
														onChange(e);
														revalidateArrivalTime();
													}}
												/>
											)}
										/>
									</GridCol>
								</Grid>
							</GridCol>

							<GridCol size={6}>
								<Grid applyContainer justify="left">
									<GridCol size={12}>
										<Heading flushSpace Elem="h5" size={3} extraClasses={styles.textAlign}>
											{EDIT_ASSIGNMENT_TEXT.actualArrival}
										</Heading>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="arrivalDate"
											control={control}
											rules={arrivalDateRules}
											render={({ field: { ref: _ref, onChange, ...field }, fieldState }) => (
												<Input
													id="arrivalDate"
													type="date"
													label={EDIT_ASSIGNMENT_TEXT.dateLabel}
													min={departureDate}
													max={maxAllowedPastDate}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
													onChange={(e) => {
														onChange(e);
														revalidateArrivalTime();
													}}
												/>
											)}
										/>
									</GridCol>

									<GridCol size={6} extraClasses={styles.marginTop}>
										<Controller
											name="arrivalTime"
											control={control}
											rules={arrivalTimeRules}
											render={({ field: { ref: _ref, ...field }, fieldState }) => (
												<Input
													id="arrivalTime"
													type="time"
													label={EDIT_ASSIGNMENT_TEXT.timeLabel}
													valid={!fieldState.invalid}
													infoError={fieldState.error?.message}
													{...field}
												/>
											)}
										/>
									</GridCol>
								</Grid>
							</GridCol>

							<GridCol size={12} extraClasses={styles.marginTop}>
								<Controller
									name="crewMember"
									control={control}
									render={({ field }) => (
										<CrewMemberAutoCompleteSingleV2
											defaultValue={field.value}
											onSelection={field.onChange}
											onReset={() => field.onChange(null)}
										/>
									)}
								/>
							</GridCol>

							<GridCol size={12} extraClasses={styles.marginTop}>
								<Controller
									name="note"
									control={control}
									render={({ field: { ref: _ref, ...field } }) => (
										<Textarea id="note" label={EDIT_ASSIGNMENT_TEXT.noteLabel} {...field} />
									)}
								/>
							</GridCol>
						</Grid>
					</form>
				</PopupSection>
			)}

			{loading && (
				<PopupSection type="icon">
					<Spinner />
				</PopupSection>
			)}

			<PopupSection type="actions" extraClasses={styles.marginTop}>
				<Button onClick={onClose}>{EDIT_ASSIGNMENT_TEXT.cancelBtn}</Button>
				<Button
					type="submit"
					appearance="primary"
					form={formId}
					disabled={loading || isSubmitDisabled}
				>
					{EDIT_ASSIGNMENT_TEXT.confirmBtn}
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default PopUpEditAssignment;
